import { useCallback } from 'react';
import useRequestState from '../../hooks/useRequestState';
import { CampaignReviewed, DisplayCampaignInformation } from '../../types/campaigns.type';
import customIcons from '../../utils/icons';
import Button from '../buttons/baseButton';
import DownloadButton from '../buttons/downloadButton';
import InformationText from '../InformationText';
import { getCampaignCsvUrl } from '../../utils/helpers';
import PostReviewCampaign from '../../services/adapters/reviewCampaignRequest';
import PostRunReviewedCampaign from '../../services/adapters/runReviewedCampaign';

export default function ReviewSendCampaign({ rowData, onClose }: { rowData: DisplayCampaignInformation, onClose: () => void }) {
  const {
    data: reviewCampaignResponse,
    error: reviewCampaignError,
    handleRequest: handleReviewRequest,
    FallbackWrapper: ReviewFallbackWrapper,
  } = useRequestState<CampaignReviewed>();

  const {
    error: sendCampaignError,
    handleRequest: handleSendRequest,
    FallbackWrapper: SendFallbackWrapper,
  } = useRequestState<void>();

  const showSendButton = rowData.reviewed || !!reviewCampaignResponse;

  const reviewCampaign = useCallback(async () => {
    const adapter = new PostReviewCampaign();
    const response = adapter.adaptResponse(rowData.campaignCommunicationId!);
    await handleReviewRequest(response);
  }, [handleReviewRequest, rowData]);

  const sendCampaign = async () => {
    const adapter = new PostRunReviewedCampaign();
    const response = await handleSendRequest(adapter.adaptResponse(rowData.campaignCommunicationId!));
    // handleRequest returns null if the request fails (void requests resolve to undefined on success).
    // sendCampaignError can't be used here: it still holds the previous render's value.
    if (response !== null) {
      onClose();
    }
  };

  const errorContent = (
    <div className="flex flex-col items-center justify-center gap-4 p-4 h-full">
      <span className="text-2xl text-pipstatus-alert">{customIcons.errorIcon}</span>
      <span className="text-md text-piptext-body whitespace-pre-line">
        {sendCampaignError?.message || reviewCampaignError?.message }
      </span>
    </div>
  );

  const sendContent = (
    <div className="flex flex-col gap-2">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary">{customIcons.mailWarningIcon}</i>
        <InformationText title="" subtitle="" />
      </div>
      <span>Are you sure you want to send the campaign?</span>
      <div className="flex justify-evenly items-center w-full">
        <SendFallbackWrapper loadingMessage="Sending Campaign...">
          <Button
            id="cancel-modal-button"
            label="Cancel"
            className="p-1 m-4 button-default w-full"
            onClick={onClose}
          />
          <Button
            id="send-campaign-modal-button"
            label="Send"
            className="p-1 m-4 w-full button-green"
            onClick={sendCampaign}
          />
        </SendFallbackWrapper>
      </div>
    </div>
  );
  const reviewContent = (
    <div className="flex flex-col gap-2">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary">{customIcons.mailWarningIcon}</i>
        <InformationText title="Review Campaign" subtitle="" />
      </div>
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        <span>{`There are ${rowData.numberOfRecipients} households eligible for this campaign. Click below to review the entire list.`}</span>
        <DownloadButton
          id={`campaign-table${rowData.campaignCommunicationId}`}
          className="!text-xs button-default !pointer-events-auto"
          fetchUrl={() => getCampaignCsvUrl(rowData)}
        />
      </div>
      <div className="flex justify-evenly items-center w-full">
        <ReviewFallbackWrapper loadingMessage="updating...">
          <Button
            id="cancel-modal-button"
            label="Cancel"
            className="p-1 m-4 button-default w-full"
            onClick={onClose}
          />
          <Button
            id="confirm-campaign-modal-button"
            label="Confirm"
            className="p-1 m-4 w-full button-green"
            onClick={reviewCampaign}
          />
        </ReviewFallbackWrapper>
      </div>
    </div>
  );
  const renderContent = () => {
    if (sendCampaignError || reviewCampaignError) {
      return errorContent;
    }
    if (showSendButton) {
      return sendContent;
    }
    return reviewContent;
  };

  return renderContent();
}
